import { useState, useCallback, memo } from "react";
import styles from "./DocumentacaoPage.module.scss";
import logoBranca from "../../assets/logo-transparente.png";

// ─── NAVEGAÇÃO ───────────────────────────────────────────────────────────────
const NAV_SECTIONS = [
  { id: "cabecalho", label: "Cabeçalho", icon: "🖼" },
  { id: "inicial",   label: "Inicial",   icon: "📊" },
  { id: "produtos",  label: "Produtos",  icon: "📦" },
  { id: "entrada",   label: "Entradas",  icon: "📥" },
  { id: "estoque",   label: "Estoque",   icon: "🗄" },
  { id: "venda",     label: "Venda",     icon: "🛒" },
  { id: "aluguel",   label: "Aluguel",   icon: "🔑" },
  {
    id: "administracao",
    label: "Administração",
    children: [
      { id: "usuarios",  label: "Usuários",        icon: "👤" },
      { id: "cliente",   label: "Área do Cliente", icon: "🏢" },
      { id: "orcamento", label: "Orçamentos",      icon: "📄" },
    ],
  },
];

// ─── SUBCOMPONENTES REUTILIZÁVEIS ────────────────────────────────────────────

const Section = memo(({ title, children }) => (
  <div className={styles.docSection}>
    <h4 className={styles.docSectionTitle}>{title}</h4>
    {children}
  </div>
));
Section.displayName = "Section";

const InfoList = memo(({ items }) => (
  <ul className={styles.docList}>
    {items.map((item, i) => (
      <li key={i} className={styles.docListItem}>{item}</li>
    ))}
  </ul>
));
InfoList.displayName = "InfoList";

const StepList = memo(({ steps }) => (
  <ol className={styles.docSteps}>
    {steps.map((step, i) => (
      <li key={i} className={styles.docStepsItem}>{step}</li>
    ))}
  </ol>
));
StepList.displayName = "StepList";

const InfoBox = memo(({ children }) => (
  <div className={styles.docInfoBox}>{children}</div>
));
InfoBox.displayName = "InfoBox";

const WarningBox = memo(({ children }) => (
  <div className={styles.docWarningBox}>{children}</div>
));
WarningBox.displayName = "WarningBox";

const PageHeader = memo(({ title, badge, mobile }) => (
  <div className={styles.docPageHeader}>
    <h2 className={styles.docTitle}>{title}</h2>
    {badge && <span className={styles.docBadge}>{badge}</span>}
    {mobile && <span className={styles.docMobileNote}>📱 Mobile</span>}
  </div>
));
PageHeader.displayName = "PageHeader";

const Key = ({ children }) => <span className={styles.docKey}>{children}</span>;
const KeySep = ({ children }) => <span className={styles.docKeySep}>{children ?? "+"}</span>;
const Tag = ({ children }) => <span className={styles.docTag}>{children}</span>;

// ─── CONTEÚDO DAS SEÇÕES ──────────────────────────────────────────────────────

const SECTION_CONTENT = {

  cabecalho: () => (
    <>
      <PageHeader title="Cabeçalho do Sistema" badge="Interface" mobile />
      <img
        src={require("../../assets/exemplo-header.png")}
        alt="Cabeçalho do sistema FluxoEasy"
        className={styles.docImage}
        loading="lazy"
      />
      <Section title="Elementos do cabeçalho">
        <InfoList items={[
          "Nome do estabelecimento — exibe o nome configurado na Área do Cliente",
          "Modo noturno — botão para alternar entre tema claro e escuro",
          "Usuário logado — exibe o nome e perfil do operador atual",
          "Botão Sair — encerra a sessão e redireciona para o login",
        ]} />
      </Section>
      <Section title="Navegação principal">
        <p className={styles.docText} style={{ marginBottom: 14 }}>
          O menu superior é o ponto de acesso a todas as áreas do sistema.
          Em dispositivos móveis, o menu colapsa automaticamente.
        </p>
        <InfoList items={[
          "Inicial — dashboard com cards e gráficos",
          "Produtos — cadastro e gestão de itens",
          "Entrada — registro de recebimento de mercadorias",
          "Estoque — visão atual do inventário",
          "Venda — PDV para finalização de vendas",
          "Aluguel — controle de contratos de locação de itens",
          "Administração — usuários, cliente e orçamentos (somente Gerentes)",
        ]} />
      </Section>
    </>
  ),

  inicial: () => (
    <>
      <PageHeader title="Página Inicial (Dashboard)" badge="Visão geral" mobile />
      <Section title="Cards de resumo">
        <p className={styles.docText} style={{ marginBottom: 14 }}>
          Exibidos no topo da página, os cards mostram os principais indicadores do dia.
        </p>
        <InfoList items={[
          "Total de Produtos Ativos — quantidade de produtos cadastrados e em uso",
          "Total de Volumes em Estoque — soma de todas as unidades disponíveis",
          "Entradas Hoje — quantidade de lotes recebidos no dia",
          "Vendas Hoje — número de transações finalizadas no dia",
        ]} />
      </Section>
      <Section title="Gráficos">
        <InfoList items={[
          "Entradas dos últimos dias — volume de recebimentos por período",
          "Valor total dos últimos dias — desempenho financeiro acumulado",
        ]} />
      </Section>
      <Section title="Relatório de Vendas">
        <StepList steps={[
          "Use os filtros para selecionar o período e categoria desejados",
          "Pesquise por produto ou operador responsável pela venda",
          "Clique em Exportar para baixar os dados em formato Excel",
        ]} />
        <InfoBox>
          O botão de exportação para Excel é exclusivo para o perfil Gerente.
        </InfoBox>
      </Section>
    </>
  ),

  produtos: () => (
    <>
      <PageHeader title="Produtos" badge="Gestão" mobile />
      <Section title="Adicionar ou editar produto">
        <StepList steps={[
          <><Tag>+ Adicionar</Tag> para criar um novo produto.</>,
          "Preencha os campos: Nome, Descrição, EAN (código de barras) e Preço de Custo.",
          <><Tag>Salvar</Tag> para confirmar ou <Tag>Cancelar</Tag> para fechar sem salvar.</>,
          <><Tag>Editar</Tag> na linha do produto para alterar um item existente.</>,
        ]} />
      </Section>
      <Section title="Ativar / Desativar produto">
        <p className={styles.docText} style={{ marginBottom: 14 }}>
          Produtos podem ser desativados sem serem removidos do sistema,
          preservando o histórico de vendas e entradas vinculadas.
        </p>
        <InfoList items={[
          <><Tag>Desativar</Tag> — o produto não aparecerá no PDV.</>,
          <><Tag>Ativar</Tag> — recoloca o produto em operação.</>,
        ]} />
        <WarningBox>
          Produtos inativos não podem ser adicionados a novas vendas ou entradas.
        </WarningBox>
      </Section>
      <Section title="Filtros e colunas da tabela">
        <p className={styles.docText} style={{ marginBottom: 14 }}>
          Use a barra de busca para filtrar em tempo real por nome, descrição ou EAN.
          Funciona tanto no desktop quanto no mobile.
        </p>
        <InfoList items={[
          "Data de criação", "Nome", "Código EAN", "Descrição",
          "Status (Ativo / Inativo)", "Ações — Editar / Ativar / Desativar",
        ]} />
      </Section>
    </>
  ),

  entrada: () => (
    <>
      <PageHeader title="Entradas" badge="Recebimento" mobile />
      <Section title="Registrar ou editar entrada">
        <StepList steps={[
          <><Tag>+ Adicionar</Tag> para registrar uma nova entrada de mercadoria.</>,
          "Preencha: Produto, Quantidade, Preço de Custo, Data de Validade e Observação.",
          "O estoque é atualizado automaticamente ao salvar.",
          <><Tag>Editar</Tag> na linha para corrigir uma entrada já lançada.</>,
        ]} />
        <InfoBox>
          Cada entrada incrementa o estoque automaticamente.
          A edição corrige tanto o registro quanto o saldo em estoque.
        </InfoBox>
      </Section>
      <Section title="Filtros">
        <p className={styles.docText}>
          Use a barra de busca para filtrar por nome do produto ou pelo operador responsável.
        </p>
      </Section>
      <Section title="Colunas da tabela">
        <InfoList items={[
          "Data de entrada", "Produto", "Código EAN", "Operador responsável",
          "Quantidade recebida", "Preço de custo unitário",
          "Data de validade", "Observação", "Ações — Editar",
        ]} />
      </Section>
    </>
  ),

  estoque: () => (
    <>
      <PageHeader title="Estoque Atual" badge="Inventário" mobile />
      <Section title="Visualizar estoque">
        <p className={styles.docText} style={{ marginBottom: 14 }}>
          Exibe em tempo real a posição atual de todos os produtos cadastrados.
          A tabela consolida entradas e deduções de venda automaticamente.
        </p>
        <InfoList items={[
          "Produto e EAN — identificação do item",
          "Quantidade — saldo atual disponível",
          "Valor de Venda — preço definido pelo Gerente (editável)",
          "Validade — data de vencimento do lote mais recente",
          "Última Atualização — data e hora da última movimentação",
        ]} />
      </Section>
      <Section title="Filtro de produtos em baixa">
        <p className={styles.docText} style={{ marginBottom: 14 }}>
          O sistema identifica produtos com quantidade abaixo do limite mínimo configurado.
        </p>
        <StepList steps={[
          <><Tag>Estoque Baixo</Tag> para exibir somente os itens críticos.</>,
          "Os produtos em baixa aparecem destacados na tabela com indicador visual.",
          "Use esse filtro para priorizar reposições antes que o item zere.",
        ]} />
        <InfoBox>
          O indicador de estoque baixo é visível para todos os perfis,
          mas somente o Gerente pode alterar o limite mínimo.
        </InfoBox>
      </Section>
      <Section title="Editar valor de venda (somente Gerente)">
        <StepList steps={[
          <><Tag>Adicionar</Tag> ou <Tag>Alterar</Tag> na coluna Valor de Venda do produto desejado.</>,
          "Digite o novo preço no modal que será exibido.",
          "O preço é atualizado imediatamente no PDV após salvar.",
        ]} />
        <WarningBox>
          Somente o perfil Gerente vê e pode alterar a coluna de Valor de Venda.
          Vendedores e Operadores de Estoque visualizam apenas quantidade e validade.
        </WarningBox>
      </Section>
      <Section title="Observações">
        <InfoList items={[
          'Produtos sem EAN cadastrado exibem "—" na coluna correspondente.',
          'Produtos sem data de validade exibem "—" na coluna de validade.',
          "Última Atualização reflete qualquer movimentação: entrada ou venda.",
        ]} />
      </Section>
    </>
  ),

  venda: () => (
    <>
      <PageHeader title="Venda (PDV)" badge="PDV" mobile />

      <Section title="Adicionar produtos à venda">
        <p className={styles.docText} style={{ marginBottom: 14 }}>
          Pesquise pelo nome ou código EAN do produto. Clique no item — ou pressione{" "}
          <Key>Enter</Key> — para adicioná-lo ao carrinho. Use <Key>F10</Key> para
          abrir a lista completa com busca e navegação por teclado.
        </p>
        <InfoList items={[
          "Produtos sem valor de venda cadastrado não podem ser selecionados",
          "Produtos com estoque zero aparecem bloqueados na lista",
          "Ao adicionar o mesmo produto duas vezes, a quantidade é incrementada automaticamente",
          "A quantidade pode ser ajustada diretamente na tabela",
          "Itens podem ser removidos pelo botão × ou com Delete quando qty = 1",
        ]} />
      </Section>

      <Section title="Múltiplas formas de pagamento">
        <p className={styles.docText} style={{ marginBottom: 14 }}>
          Uma mesma venda pode ser paga com mais de um meio de pagamento.
          O sistema solicita forma e valor parcial, repetindo até atingir o total.
        </p>
        <div className={styles.docPaymentPills}>
          {[
            { icon: "💵", label: "Dinheiro" },
            { icon: "💳", label: "Cartão Crédito" },
            { icon: "💳", label: "Cartão Débito" },
            { icon: "📱", label: "Pix" },
          ].map(({ icon, label }) => (
            <span key={label} className={styles.docPill}>
              <span className={styles.docPillIcon}>{icon}</span>
              {label}
            </span>
          ))}
        </div>
        <StepList steps={[
          <><Key>F12</Key> ou <Tag>Finalizar Venda</Tag> para iniciar o fluxo de pagamento.</>,
          "Selecione a forma de pagamento e informe o valor parcial.",
          "Se o valor não cobrir o total, o sistema pergunta se deseja adicionar outra forma.",
          "Repita até atingir o valor total da venda.",
          "Para dinheiro, informe o valor entregue — o troco é calculado automaticamente.",
          "Ao confirmar, a venda é registrada, o estoque é baixado e o carrinho é limpo.",
        ]} />
        <InfoBox>
          O rascunho da venda é salvo automaticamente. Se a página for recarregada
          acidentalmente, os itens e o desconto são restaurados.
        </InfoBox>
      </Section>

      <Section title="Totais e desconto">
        <InfoList items={[
          "Subtotal — soma de todos os itens sem desconto",
          "Desconto — campo editável disponível apenas para o perfil Gerente",
          "Total — subtotal menos o desconto aplicado",
        ]} />
        <WarningBox>
          O campo de desconto só aparece para o perfil Gerente.
          Vendedores e Operadores de Estoque não visualizam essa opção.
        </WarningBox>
      </Section>

      <Section title="Atalhos globais (desktop)">
        <p className={styles.docText} style={{ marginBottom: 14 }}>
          Todos os atalhos funcionam em qualquer momento no PDV,
          desde que um modal do sistema não esteja aberto.
        </p>
        <div className={styles.docShortcuts}>
          {[
            { keys: [<Key key="f2">F2</Key>],     desc: "Foca o campo de busca de produtos (seleciona o texto)" },
            { keys: [<Key key="f10">F10</Key>],   desc: "Abre o modal com a lista completa de produtos" },
            { keys: [<Key key="f12">F12</Key>],   desc: "Inicia o fluxo de finalização de venda" },
            { keys: [<Key key="del">Delete</Key>],desc: "Limpa toda a venda (pede confirmação) — só funciona fora de campos de texto" },
            { keys: [<Key key="esc">Esc</Key>],   desc: "Fecha o dropdown de sugestões e devolve foco ao campo de busca" },
          ].map((row, i) => (
            <div key={i} className={styles.docShortcutsRow}>
              <div className={styles.docShortcutsKeys}>{row.keys}</div>
              <span className={styles.docShortcutsDesc}>{row.desc}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Navegação no dropdown de sugestões">
        <div className={styles.docShortcuts}>
          {[
            { keys: [<Key key="d">↓</Key>, <KeySep key="s">/</KeySep>, <Key key="u">↑</Key>], desc: "Navega entre os produtos sugeridos" },
            { keys: [<Key key="e">Enter</Key>], desc: "Adiciona o item destacado — ou o único resultado, se houver apenas um" },
            { keys: [<Key key="es">Esc</Key>],  desc: "Fecha o dropdown e retorna foco ao campo de busca" },
          ].map((row, i) => (
            <div key={i} className={styles.docShortcutsRow}>
              <div className={styles.docShortcutsKeys}>{row.keys}</div>
              <span className={styles.docShortcutsDesc}>{row.desc}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Atalhos de quantidade (tabela de itens)">
        <p className={styles.docText} style={{ marginBottom: 14 }}>
          Com o foco em qualquer input de quantidade, use <Key>Tab</Key>{" "}
          / <Key>Shift+Tab</Key> para navegar entre os itens da tabela.
        </p>
        <div className={styles.docShortcuts}>
          {[
            { keys: [<Key key="t">Tab</Key>, <KeySep key="s">/</KeySep>, <Key key="st">Shift+Tab</Key>], desc: "Navega entre os inputs de quantidade de cada item" },
            { keys: [<Key key="p">+</Key>],      desc: "Incrementa +1 na quantidade do item com foco" },
            { keys: [<Key key="m">−</Key>],      desc: "Decrementa −1. Se qty = 1, remove o item da venda" },
            { keys: [<Key key="f3">F3</Key>],    desc: "Abre diálogo para digitar a quantidade exata do último item adicionado" },
            { keys: [<Key key="dl">Delete</Key>],desc: "Remove o item quando a quantidade é 1 e o campo está focado" },
          ].map((row, i) => (
            <div key={i} className={styles.docShortcutsRow}>
              <div className={styles.docShortcutsKeys}>{row.keys}</div>
              <span className={styles.docShortcutsDesc}>{row.desc}</span>
            </div>
          ))}
        </div>
        <InfoBox>
          O <Tag>F3</Tag> age sempre sobre o último item adicionado, independente
          de qual input está com foco — evita conflito com a busca do navegador.
          Após confirmar, o foco retorna automaticamente ao campo de quantidade.
        </InfoBox>
      </Section>

      <Section title="Comprovante PDF">
        <p className={styles.docText} style={{ marginBottom: 14 }}>
          Ao concluir a venda, o sistema oferece a opção de gerar um comprovante em PDF.
          O formato segue o tipo de impressão configurado na Área do Cliente.
        </p>
        <InfoList items={[
          <><Tag>A4</Tag> — ideal para e-mail, WhatsApp ou arquivo digital</>,
          <><Tag>Cupom 80mm</Tag> — estilo nota fiscal de impressora térmica</>,
          <><Tag>Bobina térmica</Tag> — para impressoras de etiqueta ou Zebra</>,
          "Se auto-imprimir estiver ativo, o PDF é gerado sem perguntar o formato",
          "Se não configurado, o sistema pergunta o formato a cada venda",
        ]} />
      </Section>
    </>
  ),

  // ─── ALUGUEL ────────────────────────────────────────────────────────────────

  aluguel: () => (
    <>
      <PageHeader title="Aluguéis" badge="Contratos" mobile />

      <Section title="Visão geral">
        <p className={styles.docText} style={{ marginBottom: 14 }}>
          A página de Aluguéis gerencia contratos de locação de itens do estabelecimento.
          Cada contrato registra o locatário, o período, os itens alugados e o controle
          financeiro de pagamentos.
        </p>
        <InfoList items={[
          "Aba Em andamento — exibe contratos ativos (reservados, confirmados e em andamento)",
          "Aba Histórico — exibe contratos encerrados (devolvidos e cancelados)",
          "Busca por nome, telefone ou número do contrato",
          "Filtro rápido por status individual",
        ]} />
      </Section>

      <Section title="Ciclo de vida do contrato">
        <p className={styles.docText} style={{ marginBottom: 14 }}>
          Todo contrato passa por um fluxo de status. Cada transição é feita
          pelos botões de ação na linha da tabela.
        </p>
        <div className={styles.docShortcuts}>
          {[
            { keys: [<Tag key="r">Reservado</Tag>],      desc: "Estado inicial ao criar um contrato. Item reservado mas ainda não retirado." },
            { keys: [<Tag key="c">Confirmado</Tag>],     desc: "Contrato confirmado. Pronto para o locatário retirar." },
            { keys: [<Tag key="a">Em andamento</Tag>],   desc: "Item foi retirado. Aluguel em curso." },
            { keys: [<Tag key="d">Devolvido</Tag>],      desc: "Item devolvido. Contrato encerrado com sucesso." },
            { keys: [<Tag key="x">Cancelado</Tag>],      desc: "Contrato cancelado. Disponível em qualquer etapa antes da devolução." },
          ].map((row, i) => (
            <div key={i} className={styles.docShortcutsRow}>
              <div className={styles.docShortcutsKeys}>{row.keys}</div>
              <span className={styles.docShortcutsDesc}>{row.desc}</span>
            </div>
          ))}
        </div>
        <InfoBox>
          O fluxo é sequencial: Reservado → Confirmado → Em andamento → Devolvido.
          Cancelar está disponível em qualquer etapa antes da devolução.
        </InfoBox>
      </Section>

      <Section title="Criar novo aluguel">
        <StepList steps={[
          <><Tag>+ Novo Aluguel</Tag> no canto superior direito para abrir o formulário.</>,
          "Preencha os dados do locatário — nome, CPF, telefone, e-mail e endereço são opcionais.",
          "Defina as datas de retirada e devolução. O sistema calcula automaticamente o número de dias.",
          "Busque e adicione os itens alugáveis. Ajuste a quantidade com os botões + e −.",
          "Informe um desconto se necessário. O total é recalculado em tempo real.",
          "Opcionalmente, registre um pagamento inicial (sinal, parcela ou quitação).",
          <><Tag>Criar aluguel</Tag> para salvar. O contrato inicia com status Reservado.</>,
        ]} />
        <InfoBox>
          O resumo financeiro no canto inferior do modal exibe subtotal, desconto,
          total, valor pago e saldo restante antes de confirmar.
        </InfoBox>
      </Section>

      <Section title="Editar contrato">
        <StepList steps={[
          <><Tag>Editar</Tag> na linha do contrato para abrir o formulário de edição.</>,
          "Todos os campos podem ser alterados: locatário, período, itens e desconto.",
          <><Tag>Salvar alterações</Tag> para confirmar.</>,
        ]} />
        <WarningBox>
          A edição não altera o status nem os pagamentos já registrados.
          Para registrar novos pagamentos, use o botão <Tag>Pagamento</Tag> na linha da tabela.
        </WarningBox>
      </Section>

      <Section title="Registrar pagamento">
        <StepList steps={[
          <><Tag>Pagamento</Tag> na linha do contrato para abrir o modal de pagamento.</>,
          "O modal exibe o total do contrato, o valor já pago e o saldo restante.",
          "Selecione o tipo (Sinal, Parcela ou Quitação) e a forma (Pix, Dinheiro, Cartão).",
          "Informe o valor — o máximo permitido é o saldo restante.",
          <><Tag>Confirmar pagamento</Tag> para registrar.</>,
        ]} />
        <InfoBox>
          O badge de pagamento na tabela atualiza automaticamente após cada registro,
          exibindo "✓ Quitado" ou o valor ainda em aberto.
        </InfoBox>
      </Section>

      <Section title="Colunas da tabela">
        <InfoList items={[
          "Nº — número do contrato formatado com zeros à esquerda (ex: #00042)",
          "Locatário — nome e telefone do locatário",
          "Período — datas de retirada e devolução",
          "Itens — resumo dos produtos alugados (exibe até 2, com contador para o restante)",
          "Total — valor total do contrato",
          "Pagamento — badge indicando se está quitado ou o saldo em aberto",
          "Status — status atual do contrato",
          "Ações — Editar, Pagamento e botões de transição de status",
        ]} />
      </Section>

      <Section title="Observações">
        <InfoList items={[
          "O botão Pagamento não aparece em contratos Devolvidos ou Cancelados",
          "Contratos cancelados são exibidos com visual esmaecido na tabela",
          "A contagem de dias leva em conta as horas — períodos menores que 24h contam como 1 dia",
          "Itens alugáveis são os produtos cadastrados com a opção de locação habilitada",
        ]} />
      </Section>
    </>
  ),

  // ─── ADMINISTRAÇÃO ───────────────────────────────────────────────────────────

  usuarios: () => (
    <>
      <PageHeader title="Usuários" badge="Administração" />
      <Section title="Perfis de acesso">
        <p className={styles.docText} style={{ marginBottom: 14 }}>
          O sistema possui três perfis com permissões distintas.
          O perfil define o que cada operador pode visualizar e executar.
        </p>
        <table className={styles.docRolesTable}>
          <thead>
            <tr><th>Perfil</th><th>Acesso</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><span className={`${styles.docRoleChip} ${styles.docRoleChipAdmin}`}>Gerente</span></td>
              <td>Acesso total — dashboard, produtos, entradas, estoque (com edição de preços), PDV com desconto, aluguéis, administração completa e exportação de relatórios.</td>
            </tr>
            <tr>
              <td><span className={`${styles.docRoleChip} ${styles.docRoleChipVendedor}`}>Vendedor(a)</span></td>
              <td>Acesso ao PDV, consulta de produtos e estoque (sem edição de preços). Não acessa a área de Administração.</td>
            </tr>
            <tr>
              <td><span className={`${styles.docRoleChip} ${styles.docRoleChipEstoque}`}>Op. de Estoque</span></td>
              <td>Acesso ao cadastro de entradas e consulta de estoque. Não acessa PDV nem Administração.</td>
            </tr>
          </tbody>
        </table>
      </Section>
      <Section title="Adicionar ou editar usuário">
        <StepList steps={[
          <><Tag>+ Adicionar</Tag> para criar um novo usuário.</>,
          "Preencha: nome completo, login, cargo descritivo e perfil de acesso.",
          "Defina uma senha — obrigatória na criação. Pode ser omitida ao editar.",
          <><Tag>Editar</Tag> na linha do usuário para alterar qualquer dado existente.</>,
          "Todos os campos podem ser alterados, incluindo perfil e login.",
        ]} />
        <InfoBox>
          Um usuário pode ser completamente redefinido — nome, login, cargo, perfil e senha —
          sem necessidade de criar um novo cadastro.
        </InfoBox>
      </Section>
      <Section title="Ativar / Desativar usuário">
        <p className={styles.docText} style={{ marginBottom: 14 }}>
          Usuários não podem ser removidos do sistema para preservar o histórico
          de operações vinculadas. Em vez disso, podem ser desativados.
        </p>
        <InfoList items={[
          <><Tag>Desativar</Tag> — o usuário perde acesso imediatamente ao tentar logar.</>,
          <><Tag>Ativar</Tag> — restaura o acesso do operador.</>,
          "Uma confirmação é exibida antes de qualquer alteração de status.",
          "A lista atualiza automaticamente após cada ação.",
        ]} />
        <WarningBox>
          Um usuário desativado não consegue acessar o sistema, mas todas as suas
          operações anteriores (vendas, entradas) permanecem registradas no histórico.
        </WarningBox>
      </Section>
      <Section title="Filtros">
        <p className={styles.docText}>
          Use a barra de busca para filtrar em tempo real por nome, login, cargo ou perfil.
        </p>
      </Section>
    </>
  ),

  cliente: () => (
    <>
      <PageHeader title="Área do Cliente" badge="Administração" />
      <Section title="Dados exibidos">
        <InfoList items={[
          "Nome do estabelecimento — aparece no cabeçalho e nos documentos",
          "CNPJ ou CPF — utilizado em orçamentos e notas",
          "E-mail e Telefone — contato para uso interno",
          "Endereço completo — utilizado em orçamentos e documentos",
          "Logo — exibida no cabeçalho do sistema, orçamentos e PDFs",
        ]} />
      </Section>
      <Section title="Editar dados do estabelecimento">
        <StepList steps={[
          <><Tag>Editar</Tag> para abrir o formulário de dados.</>,
          "Atualize os campos desejados — todos são opcionais na edição.",
          "Salve — as informações são atualizadas em tempo real no sistema.",
        ]} />
      </Section>
      <Section title="Gerenciar logo">
        <StepList steps={[
          "Clique diretamente sobre a imagem da logo para selecionar um novo arquivo.",
          "Formatos aceitos: PNG, JPG ou SVG. Recomendado: fundo transparente (PNG).",
          <><Tag>Remover logo</Tag> para excluir a imagem atual e usar o padrão do sistema.</>,
        ]} />
        <InfoBox>
          O upload é processado via FormData com autenticação JWT.
          A logo é salva e exibida imediatamente após o envio.
        </InfoBox>
      </Section>
      <Section title="Tipo de impressão">
        <p className={styles.docText} style={{ marginBottom: 14 }}>
          Configure o formato padrão de impressão para documentos gerados pelo sistema.
        </p>
        <InfoList items={[
          <><Tag>A4</Tag> — formato padrão para orçamentos e relatórios completos.</>,
          <><Tag>Cupom (58mm / 80mm)</Tag> — formato para impressoras térmicas de PDV.</>,
          "A configuração afeta orçamentos, comprovantes de venda e demais documentos.",
          "Cada formato gera um layout otimizado automaticamente.",
        ]} />
        <InfoBox>
          O tipo de impressão configurado aqui é o padrão do sistema, mas pode ser
          alterado pontualmente no momento de gerar cada documento.
        </InfoBox>
      </Section>
    </>
  ),

  orcamento: () => (
    <>
      <PageHeader title="Orçamentos" badge="Administração" />
      <Section title="Dados do cliente no orçamento">
        <p className={styles.docText} style={{ marginBottom: 14 }}>
          O orçamento é gerado com as informações cadastradas na Área do Cliente.
          Certifique-se de que os dados estão atualizados antes de gerar o PDF.
        </p>
        <InfoList items={[
          "Nome, CNPJ/CPF, E-mail, Telefone e Endereço puxados automaticamente",
          "Logo do estabelecimento incorporada ao documento em Base64",
          'Exibe "Carregando..." enquanto os dados são buscados da API',
        ]} />
      </Section>
      <Section title="Adicionar produtos ao orçamento">
        <StepList steps={[
          "Preencha o nome do produto, a quantidade e o preço unitário.",
          <><Tag>Adicionar Produto</Tag> para inserir o item na lista.</>,
          "Repita para quantos produtos forem necessários.",
          "Clique no ícone de exclusão para remover um item — o total recalcula automaticamente.",
        ]} />
      </Section>
      <Section title="Personalizar cor do orçamento">
        <p className={styles.docText} style={{ marginBottom: 14 }}>
          Escolha uma cor de destaque para o orçamento. A cor é aplicada no cabeçalho,
          nos títulos e nos elementos visuais do PDF gerado.
        </p>
        <div className={styles.docSwatches}>
          {[
            "#1A56DB", "#0E7D5B", "#B91C1C", "#92400E",
            "#6D28D9", "#0369A1", "#0F766E", "#BE185D",
            "#1E293B", "#374151",
          ].map((color) => (
            <span
              key={color}
              className={styles.docSwatch}
              style={{ background: color }}
              title={color}
            />
          ))}
        </div>
        <InfoBox>
          A cor selecionada é aplicada apenas ao PDF — não altera o tema do sistema.
          O preview é atualizado em tempo real conforme a cor é escolhida.
        </InfoBox>
      </Section>
      <Section title="Observações e rodapé">
        <InfoList items={[
          "Observações — campo de texto livre exibido no corpo do orçamento",
          "Mensagem de rodapé — texto exibido ao final do PDF (ex: validade do orçamento)",
        ]} />
      </Section>
      <Section title="Gerar e baixar PDF">
        <StepList steps={[
          "O preview do orçamento é exibido em tempo real na lateral da página.",
          "Confira os dados, itens, cor e totais antes de gerar o documento final.",
          <><Tag>Baixar PDF</Tag> para fazer o download do arquivo.</>,
        ]} />
        <InfoBox>
          O total é recalculado automaticamente sempre que um produto é adicionado,
          removido ou tem sua quantidade alterada.
        </InfoBox>
      </Section>
    </>
  ),
};

// ─── COMPONENTES DE NAVEGAÇÃO ────────────────────────────────────────────────

const NavItem = memo(({ section, isActive, onClick }) => (
  <button
    className={`${styles.navItem}${isActive ? ` ${styles.navItemActive}` : ""}`}
    onClick={() => onClick(section.id)}
    aria-current={isActive ? "page" : undefined}
    type="button"
  >
    <span className={styles.navItemIcon} aria-hidden="true">{section.icon}</span>
    <span className={styles.navItemLabel}>{section.label}</span>
  </button>
));
NavItem.displayName = "NavItem";

const NavGroup = memo(({ section, selected, onSelect }) => (
  <div className={styles.navGroup} role="group" aria-label={section.label}>
    <span className={styles.navGroupLabel} aria-hidden="true">{section.label}</span>
    <div className={styles.navGroupChildren}>
      {section.children.map((child) => (
        <NavItem
          key={child.id}
          section={child}
          isActive={selected === child.id}
          onClick={onSelect}
        />
      ))}
    </div>
  </div>
));
NavGroup.displayName = "NavGroup";

// ─── COMPONENTE PRINCIPAL ────────────────────────────────────────────────────

export default function DocumentacaoPage() {
  const [selected, setSelected] = useState("inicial");

  const handleSelect = useCallback((id) => {
    setSelected(id);
    document.getElementById("doc-content-top")?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const ContentComponent = SECTION_CONTENT[selected] ?? null;

  return (
    <div className={styles.docLayout}>

      <aside className={styles.docSidebar} aria-label="Navegação da documentação">
        <div className={styles.docSidebarHeader}>
          <img src={logoBranca} alt="FluxoEasy" className={styles.docSidebarLogo} />
        </div>
        <nav className={styles.docSidebarNav} aria-label="Seções da documentação">
          {NAV_SECTIONS.map((section) =>
            section.children ? (
              <NavGroup
                key={section.id}
                section={section}
                selected={selected}
                onSelect={handleSelect}
              />
            ) : (
              <NavItem
                key={section.id}
                section={section}
                isActive={selected === section.id}
                onClick={handleSelect}
              />
            )
          )}
        </nav>
      </aside>

      <main
        className={styles.docMain}
        id="doc-content"
        aria-label="Conteúdo da documentação"
        tabIndex={-1}
      >
        <div className={styles.docContent}>
          <div id="doc-content-top" aria-hidden="true" />
          {ContentComponent ? <ContentComponent /> : (
            <p className={styles.docText}>Seção não encontrada.</p>
          )}
        </div>
      </main>
    </div>
  );
}